<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="hero-background">
          <div class="hero-pattern"></div>
        </div>
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <router-link to="/" class="breadcrumb-link">Home</router-link>
              <span class="breadcrumb-separator">/</span>
              <router-link to="/services" class="breadcrumb-link">Services</router-link>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Alcoholic Drinks Licence</span>
            </nav>
            <div class="hero-title-section">
              <div class="hero-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M8 22h8"></path>
                  <path d="M12 11v11"></path>
                  <path d="M5 3l5 7h4l5-7"></path>
                  <path d="M6.5 7h11"></path>
                  <path d="M10 10a4 4 0 1 0 4 0"></path>
                </svg>
              </div>
              <div>
                <h1 class="page-title">Alcoholic Drinks Licence</h1>
                <p class="page-subtitle">Apply for a new liquor license or renew your existing license</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Progress Steps -->
      <section class="progress-section" v-if="!applicationComplete">
        <div class="container">
          <div class="progress-bar">
            <div class="progress-track">
              <div class="progress-fill" :style="{ width: progressWidth }"></div>
            </div>
            <div class="progress-steps">
              <div
                  v-for="(step, index) in steps"
                  :key="index"
                  class="progress-step"
                  :class="{
                  active: currentStep === index + 1,
                  completed: currentStep > index + 1
                }"
              >
                <div class="step-marker">
                  <svg v-if="currentStep > index + 1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <span v-else>{{ index + 1 }}</span>
                </div>
                <span class="step-label">{{ step }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Main Content Area -->
      <section class="form-section">
        <div class="container">
          <div class="content-grid">
            <!-- Left Column - Form -->
            <div class="form-column">

              <!-- Step 1: Choose Method -->
              <div v-if="currentStep === 1" class="form-card">
                <div class="card-header">
                  <h2>How would you like to apply?</h2>
                  <p>Select how you want to register your establishment for liquor licensing</p>
                </div>
                <div class="card-body">
                  <div class="method-options">
                    <div
                        class="method-card"
                        :class="{ selected: selectedMethod === 'search' }"
                        @click="selectedMethod = 'search'"
                    >
                      <div class="method-icon blue">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                      </div>
                      <div class="method-content">
                        <h3>Search Existing Business</h3>
                        <p>Find your already registered business and apply for a liquor license</p>
                      </div>
                      <div class="method-check" v-if="selectedMethod === 'search'">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                    </div>

                    <div
                        class="method-card"
                        :class="{ selected: selectedMethod === 'register' }"
                        @click="selectedMethod = 'register'"
                    >
                      <div class="method-icon green">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="8.5" cy="7" r="4"></circle>
                          <line x1="20" y1="8" x2="20" y2="14"></line>
                          <line x1="23" y1="11" x2="17" y2="11"></line>
                        </svg>
                      </div>
                      <div class="method-content">
                        <h3>New/Unregistered Business</h3>
                        <p>Register a new establishment that doesn't have a business permit yet</p>
                      </div>
                      <div class="method-check" v-if="selectedMethod === 'register'">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div class="form-actions">
                    <button
                        class="btn btn-primary btn-lg"
                        :disabled="!selectedMethod"
                        @click="proceedToStep2"
                    >
                      Continue
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                        <polyline points="12 5 19 12 12 19"></polyline>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Step 2: Business Search (if search method) -->
              <div v-if="currentStep === 2 && selectedMethod === 'search'" class="form-card">
                <div class="card-header">
                  <button class="back-btn" @click="currentStep = 1">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="19" y1="12" x2="5" y2="12"></line>
                      <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                    Back
                  </button>
                  <h2>Search for Your Business</h2>
                  <p>Enter your business name or Business ID to find your registered business</p>
                </div>
                <div class="card-body">
                  <!-- Search Input -->
                  <div class="search-box">
                    <div class="search-input-wrapper">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                      <input
                          type="text"
                          v-model="searchKeyword"
                          placeholder="Enter business name or Business ID..."
                          @keyup.enter="searchBusinesses"
                          class="search-input"
                      />
                      <button v-if="searchKeyword" @click="searchKeyword = ''" class="clear-btn">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                    <button class="btn btn-primary" @click="searchBusinesses" :disabled="!searchKeyword || searchKeyword.length < 2">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                      Search
                    </button>
                  </div>

                  <!-- Loading State -->
                  <div v-if="businessSearch" class="loading-state">
                    <div class="loader-spinner"></div>
                    <p>Searching for businesses...</p>
                  </div>

                  <!-- Search Results List -->
                  <div v-if="businessSearchResults.length > 0 && !businessDetailsResult" class="search-results">
                    <div class="results-header">
                      <span class="results-count">Found {{ businessSearchResults.length }} result(s)</span>
                      <span class="results-hint">Click to select a business</span>
                    </div>
                    <div class="results-list">
                      <div
                          v-for="(item, index) in businessSearchResults"
                          :key="index"
                          class="result-item"
                          @click="selectBusiness(item)"
                      >
                        <div class="result-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                          </svg>
                        </div>
                        <div class="result-content">
                          <h4>{{ item.businessName }}</h4>
                          <p>
                            <span class="result-tag">ID: {{ item.businessID }}</span>
                            <span class="result-location">{{ item.subCountyName }}, {{ item.wardName }}</span>
                          </p>
                        </div>
                        <div class="result-action">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="9 18 15 12 9 6"></polyline>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- No Results -->
                  <div v-if="noResultsFound" class="no-results">
                    <div class="no-results-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        <line x1="8" y1="8" x2="14" y2="14"></line>
                        <line x1="14" y1="8" x2="8" y2="14"></line>
                      </svg>
                    </div>
                    <h3>No Business Found</h3>
                    <p>No business found matching "<strong>{{ searchKeyword }}</strong>"</p>
                    <button class="btn btn-outline" @click="switchToRegister">
                      Register as New Business
                    </button>
                  </div>

                  <!-- Selected Business Details -->
                  <div v-if="businessDetailsResult" class="selected-business">
                    <div class="selected-header">
                      <button class="back-link" @click="backToSearchResults">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="19" y1="12" x2="5" y2="12"></line>
                          <polyline points="12 19 5 12 12 5"></polyline>
                        </svg>
                        Back to Results
                      </button>
                      <span class="selected-badge">Selected Business</span>
                    </div>

                    <div class="business-details-card">
                      <div class="business-header">
                        <div class="business-avatar">
                          {{ businessDetails.businessName ? businessDetails.businessName.charAt(0).toUpperCase() : 'B' }}
                        </div>
                        <div class="business-title">
                          <h3>{{ businessDetails.businessName }}</h3>
                          <span class="business-id">Business ID: {{ businessDetails.businessID }}</span>
                        </div>
                      </div>

                      <div class="details-grid">
                        <div class="detail-item">
                          <label>Sub County</label>
                          <span>{{ businessDetails.subCountyName }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Ward</label>
                          <span>{{ businessDetails.wardName }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Physical Address</label>
                          <span>{{ businessDetails.physicalAddress || 'N/A' }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Building/Premises</label>
                          <span>{{ businessDetails.buildingName || 'N/A' }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Applicant Name</label>
                          <span>{{ businessDetails.fullNames || 'N/A' }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Phone Number</label>
                          <span>{{ businessDetails.ownerPhone || 'N/A' }}</span>
                        </div>
                        <div class="detail-item full-width">
                          <label>Email Address</label>
                          <span>{{ businessDetails.ownerEmail || 'N/A' }}</span>
                        </div>
                      </div>

                      <div class="form-actions">
                        <button class="btn btn-primary btn-lg" @click="registerBusiness" :disabled="generatingInvoice">
                          <span v-if="!generatingInvoice">
                            Submit Application
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <line x1="5" y1="12" x2="19" y2="12"></line>
                              <polyline points="12 5 19 12 12 19"></polyline>
                            </svg>
                          </span>
                          <span v-else class="loading-text">
                            <span class="spinner-sm"></span>
                            Processing...
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 2: Registration Form (if register method) -->
              <div v-if="currentStep === 2 && selectedMethod === 'register'" class="form-card">
                <div class="card-header">
                  <button class="back-btn" @click="currentStep = 1">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="19" y1="12" x2="5" y2="12"></line>
                      <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                    Back
                  </button>
                  <h2>Establishment Details</h2>
                  <p>Enter the details of your establishment for liquor licensing</p>
                </div>
                <div class="card-body">
                  <div class="form-grid">
                    <!-- Establishment Name -->
                    <div class="form-group full-width">
                      <label class="form-label required">Name of Establishment/Proprietor</label>
                      <input
                          type="text"
                          v-model="businessDetails.businessName"
                          class="form-input"
                          placeholder="Enter the business/establishment name"
                      />
                    </div>

                    <!-- Building Name -->
                    <div class="form-group">
                      <label class="form-label required">Name of Premises/Building</label>
                      <input
                          type="text"
                          v-model="businessDetails.buildingName"
                          class="form-input"
                          placeholder="Building or premises name"
                      />
                    </div>

                    <!-- Sub County -->
                    <div class="form-group">
                      <label class="form-label required">Sub County</label>
                      <select v-model="subCounty" class="form-select">
                        <option value="">Select Sub County</option>
                        <option v-for="(item, index) in subCounties" :value="item" :key="index">
                          {{ item.subCountyName }}
                        </option>
                      </select>
                    </div>

                    <!-- Ward -->
                    <div class="form-group">
                      <label class="form-label required">Ward</label>
                      <select v-model="ward" class="form-select" :disabled="!subCounty">
                        <option value="">Select Ward</option>
                        <option v-for="(item, index) in wards" :value="item" :key="index">
                          {{ item.wardName }}
                        </option>
                      </select>
                    </div>

                    <!-- Applicant Name -->
                    <div class="form-group">
                      <label class="form-label required">Applicant's Full Name</label>
                      <input
                          type="text"
                          v-model="businessDetails.fullNames"
                          class="form-input"
                          placeholder="e.g. John Doe"
                      />
                    </div>

                    <!-- Postal Address -->
                    <div class="form-group">
                      <label class="form-label">Postal Address</label>
                      <input
                          type="text"
                          v-model="businessDetails.postalAddress"
                          class="form-input"
                          placeholder="e.g. P.O. Box 123-00100"
                      />
                    </div>

                    <!-- Physical Address -->
                    <div class="form-group full-width">
                      <label class="form-label required">Physical Address</label>
                      <textarea
                          v-model="businessDetails.physicalAddress"
                          class="form-textarea"
                          placeholder="Enter the physical location of the establishment"
                          rows="3"
                      ></textarea>
                    </div>

                    <!-- Phone Number -->
                    <div class="form-group">
                      <label class="form-label required">Phone Number</label>
                      <input
                          type="tel"
                          v-model="businessDetails.ownerPhone"
                          class="form-input"
                          placeholder="e.g. 0712345678"
                      />
                    </div>

                    <!-- Email -->
                    <div class="form-group">
                      <label class="form-label">Email Address</label>
                      <input
                          type="email"
                          v-model="businessDetails.ownerEmail"
                          class="form-input"
                          placeholder="email@example.com"
                      />
                    </div>
                  </div>

                  <div class="form-actions">
                    <button
                        class="btn btn-primary btn-lg"
                        @click="registerBusiness"
                        :disabled="!isFormValid || generatingInvoice"
                    >
                      <span v-if="!generatingInvoice">
                        Submit Application
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </span>
                      <span v-else class="loading-text">
                        <span class="spinner-sm"></span>
                        Submitting...
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Step 3: Invoice (only show if amount > 0) -->
              <div v-if="currentStep === 3 && !isZeroAmount" class="form-card">
                <div class="card-header">
                  <h2>Invoice Generated</h2>
                  <p>Review and pay your liquor license application fee</p>
                </div>
                <div class="card-body">
                  <!-- Invoice Display -->
                  <div class="invoice-card">
                    <div class="invoice-header">
                      <div class="invoice-title">
                        <span class="invoice-label">Invoice Number</span>
                        <span class="invoice-number">{{ billDetails.billNo }}</span>
                      </div>
                      <div class="invoice-status pending">Pending Payment</div>
                    </div>

                    <div class="invoice-details">
                      <div class="invoice-row">
                        <span class="label">Establishment</span>
                        <span class="value">{{ billDetails.description }}</span>
                      </div>
                      <div class="invoice-row">
                        <span class="label">Phone</span>
                        <span class="value">{{ billDetails.customerPhoneNumber }}</span>
                      </div>
                      <div class="invoice-row">
                        <span class="label">Date</span>
                        <span class="value">{{ billDetails.dateCreated }}</span>
                      </div>
                    </div>

                    <div class="invoice-items">
                      <div class="items-header">
                        <span>Description</span>
                        <span>Amount</span>
                      </div>
                      <div class="item-row" v-for="(item, index) in billInfo" :key="index">
                        <span class="item-desc">{{ item.feeDescription }}</span>
                        <span class="item-amount">{{ formatCurrency(item.billTotal) }}</span>
                      </div>
                    </div>

                    <div class="invoice-total">
                      <div class="total-row">
                        <span>Subtotal</span>
                        <span>{{ formatCurrency(billDetails.detailAmount) }}</span>
                      </div>
                      <div class="total-row">
                        <span>Amount Paid</span>
                        <span>{{ formatCurrency(billDetails.receiptAmount || 0) }}</span>
                      </div>
                      <div class="total-row final">
                        <span>Total Payable</span>
                        <span class="total-amount">{{ formatCurrency(billDetails.billBalance) }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="form-actions split">
                    <button class="btn btn-outline" @click="printBill">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                      </svg>
                      Print Invoice
                    </button>
                    <button class="btn btn-primary btn-lg" @click="showPaymentModal = true">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                      Pay Now
                    </button>
                  </div>
                </div>
              </div>

              <!-- Step 3: Application Received (when amount is 0) -->
              <div v-if="currentStep === 3 && isZeroAmount" class="form-card success-card">
                <div class="success-content">
                  <div class="success-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                  <h2>Application Received!</h2>
                  <p>Your liquor license application has been submitted successfully.</p>

                  <div class="receipt-info">
                    <div class="info-row">
                      <span>Reference Number</span>
                      <strong>{{ billDetails.billNo }}</strong>
                    </div>
                    <div class="info-row">
                      <span>Establishment</span>
                      <strong>{{ billDetails.description || businessDetails.businessName }}</strong>
                    </div>
                    <div class="info-row">
                      <span>Phone Number</span>
                      <strong>{{ billDetails.customerPhoneNumber || businessDetails.ownerPhone }}</strong>
                    </div>
                    <div class="info-row">
                      <span>Date Submitted</span>
                      <strong>{{ formatDate(billDetails.dateCreated) }}</strong>
                    </div>
                  </div>

                  <div class="next-steps">
                    <h4>What's Next?</h4>
                    <ul>
                      <li>Your application will be reviewed by the licensing department</li>
                      <li>An inspection may be scheduled for your premises</li>
                      <li>You will be notified via SMS once your license is ready</li>
                      <li>You can track your application status using the reference number above</li>
                    </ul>
                  </div>

                  <div class="success-actions">
                    <button class="btn btn-primary" @click="printConfirmation">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                      </svg>
                      Print Confirmation
                    </button>
                    <router-link to="/services" class="btn btn-outline">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                      </svg>
                      Back to Services
                    </router-link>
                    <button class="btn btn-outline" @click="startNewApplication">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                      New Application
                    </button>
                  </div>
                </div>
              </div>

              <!-- Application Complete -->
              <div v-if="applicationComplete" class="form-card success-card">
                <div class="success-content">
                  <div class="success-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                  <h2>Payment Successful!</h2>
                  <p>Your liquor license application has been submitted successfully.</p>

                  <div class="receipt-info">
                    <div class="info-row">
                      <span>Receipt Number</span>
                      <strong>{{ receiptDetails.receiptNo }}</strong>
                    </div>
                    <div class="info-row">
                      <span>Amount Paid</span>
                      <strong>{{ formatCurrency(receiptDetails.receiptAmount) }}</strong>
                    </div>
                    <div class="info-row">
                      <span>Bill Number</span>
                      <strong>{{ receiptDetails.billNo }}</strong>
                    </div>
                  </div>

                  <div class="next-steps">
                    <h4>What's Next?</h4>
                    <ul>
                      <li>Your application will be reviewed by the licensing department</li>
                      <li>An inspection may be scheduled for your premises</li>
                      <li>You will be notified via SMS once your license is ready</li>
                      <li>You can track your application status using the reference number</li>
                    </ul>
                  </div>

                  <div class="success-actions">
                    <button class="btn btn-primary" @click="printReceipt">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                      </svg>
                      Print Receipt
                    </button>
                    <router-link to="/services" class="btn btn-outline">
                      Back to Services
                    </router-link>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column - Info -->
            <div class="info-column">
              <!-- Quick Actions Card -->
              <div class="info-card quick-actions-card">
                <div class="info-card-header">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                  <h3>Quick Actions</h3>
                </div>
                <div class="quick-actions-list">
                  <router-link to="/print-liquor-license" class="quick-action-item">
                    <div class="quick-action-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                      </svg>
                    </div>
                    <div class="quick-action-content">
                      <span class="quick-action-title">Print Liquor Licence</span>
                      <span class="quick-action-desc">View and print your existing licence</span>
                    </div>
                    <svg class="quick-action-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </router-link>
                  <router-link to="/track-liquor-application" class="quick-action-item">
                    <div class="quick-action-icon orange">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                    </div>
                    <div class="quick-action-content">
                      <span class="quick-action-title">Track Application</span>
                      <span class="quick-action-desc">Check your application status</span>
                    </div>
                    <svg class="quick-action-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </router-link>
                </div>
              </div>

              <!-- Requirements Card -->
              <div class="info-card">
                <div class="info-card-header">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                  </svg>
                  <h3>Requirements</h3>
                </div>
                <ul class="requirements-list">
                  <li>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Valid National ID or Passport
                  </li>
                  <li>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Business Permit (if available)
                  </li>
                  <li>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Premises Lease Agreement
                  </li>
                  <li>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Fire Safety Certificate
                  </li>
                  <li>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Public Health Certificate
                  </li>
                </ul>
              </div>

              <!-- Process Card -->
              <div class="info-card">
                <div class="info-card-header">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <h3>Processing Time</h3>
                </div>
                <div class="process-info">
                  <div class="process-item">
                    <span class="process-label">Application Review</span>
                    <span class="process-time">2-3 days</span>
                  </div>
                  <div class="process-item">
                    <span class="process-label">Premises Inspection</span>
                    <span class="process-time">5-7 days</span>
                  </div>
                  <div class="process-item">
                    <span class="process-label">Vetting Committee</span>
                    <span class="process-time">7-14 days</span>
                  </div>
                  <div class="process-item">
                    <span class="process-label">License Issuance</span>
                    <span class="process-time">1-2 days</span>
                  </div>
                </div>
              </div>

              <!-- Help Card -->
              <div class="info-card help-card">
                <div class="info-card-header">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  <h3>Need Help?</h3>
                </div>
                <p>Having trouble with your application? Our support team is here to help.</p>
                <router-link to="/contact" class="help-link">
                  Contact Support
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- Payment Modal -->
    <div class="modal-overlay" v-if="showPaymentModal" @click.self="showPaymentModal = false">
      <div class="modal-container">
        <div class="modal-header">
          <h3>Payment</h3>
          <button class="modal-close" @click="showPaymentModal = false">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <!-- Payment Method Selection -->
          <div v-if="paymentStep === 'method'" class="payment-methods">
            <div
                class="payment-method"
                :class="{ selected: paymentData.paymentMode === 'MPESA' }"
                @click="paymentData.paymentMode = 'MPESA'"
            >
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/15/M-PESA_LOGO-01.svg/1200px-M-PESA_LOGO-01.svg.png" alt="M-Pesa" class="method-logo" />
              <div class="method-info">
                <h4>M-Pesa</h4>
                <p>Pay using M-Pesa mobile money</p>
              </div>
            </div>

            <div v-if="paymentData.paymentMode === 'MPESA'" class="phone-input-section">
              <label>Enter M-Pesa Phone Number</label>
              <div class="phone-input-wrapper">
                <span class="phone-prefix">+254</span>
                <input
                    type="tel"
                    v-model="phoneNumber"
                    placeholder="712345678"
                    class="phone-input"
                    maxlength="10"
                />
              </div>
            </div>

            <button
                class="btn btn-primary btn-block"
                :disabled="!paymentData.paymentMode || (paymentData.paymentMode === 'MPESA' && !phoneNumber)"
                @click="paymentStep = 'confirm'"
            >
              Continue
            </button>
          </div>

          <!-- Payment Confirmation -->
          <div v-if="paymentStep === 'confirm'" class="payment-confirm">
            <div class="confirm-summary">
              <div class="summary-row">
                <span>Bill Number</span>
                <span>{{ billDetails.billNo }}</span>
              </div>
              <div class="summary-row">
                <span>Payment Method</span>
                <span>{{ paymentData.paymentMode }}</span>
              </div>
              <div class="summary-row" v-if="paymentData.paymentMode === 'MPESA'">
                <span>Phone Number</span>
                <span>{{ phoneNumber }}</span>
              </div>
              <div class="summary-row total">
                <span>Amount to Pay</span>
                <span>{{ formatCurrency(billDetails.billBalance) }}</span>
              </div>
            </div>

            <div class="confirm-note" v-if="paymentData.paymentMode === 'MPESA'">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              <p>A payment request will be sent to <strong>{{ phoneNumber }}</strong>. Please check your phone and enter your M-Pesa PIN to complete the payment.</p>
            </div>

            <p class="payment-message" v-if="message">{{ message }}</p>

            <div class="confirm-actions">
              <button class="btn btn-outline" @click="paymentStep = 'method'">Back</button>
              <button class="btn btn-primary" @click="makePayment" :disabled="processingPayment">
                <span v-if="!processingPayment">Send Payment Request</span>
                <span v-else class="loading-text">
                  <span class="spinner-sm"></span>
                  {{ message || 'Processing...' }}
                </span>
              </button>
            </div>
          </div>

          <!-- Payment Success -->
          <div v-if="paymentStep === 'success'" class="payment-success">
            <div class="success-icon-lg">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <h3>Payment Successful!</h3>
            <p>Your payment of <strong>{{ formatCurrency(receiptDetails.receiptAmount) }}</strong> has been received.</p>
            <p class="receipt-number">Receipt No: <strong>{{ receiptDetails.receiptNo }}</strong></p>
            <button class="btn btn-primary btn-block" @click="completeApplication">
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>

    <FooterComponent />
  </div>
</template>

<script>
/* eslint-disable */
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";
import { execute, biller, trade, liquor } from "@/api";

export default {
  name: "LiquorLicense",
  components: {
    HeaderComponent,
    FooterComponent
  },
  data() {
    return {
      currentStep: 1,
      steps: ['Choose Method', 'Business Details', 'Confirmation'],
      selectedMethod: '',
      searchKeyword: '',
      businessSearch: false,
      businessDetailsResult: false,
      noResultsFound: false,
      businessSearchResults: [],
      generatingInvoice: false,
      showPaymentModal: false,
      paymentStep: 'method',
      processingPayment: false,
      applicationComplete: false,
      message: '',
      phoneNumber: '',

      businessDetails: {
        id: "",
        businessID: "",
        businessName: "",
        subCountyID: "",
        subCountyName: "",
        wardID: "",
        wardName: "",
        plotNumber: "",
        physicalAddress: "",
        buildingName: "",
        postalAddress: "",
        postalCode: "",
        fullNames: "",
        ownerPhone: "",
        ownerEmail: ""
      },

      subCounty: '',
      ward: '',
      subCounties: [],
      wards: [],

      billDetails: {
        id: '',
        billNo: '',
        incomeTypeID: '',
        incomeTypeDescription: '',
        costCenterNo: '',
        accountNo: '',
        feeID: '',
        detailAmount: '',
        receiptAmount: '',
        billBalance: '',
        wardID: '',
        subCountyID: '',
        status: '',
        dateCreated: '',
        description: '',
        customerPhoneNumber: ''
      },
      billInfo: [],

      receiptDetails: {
        receiptNo: null,
        billNo: '',
        receiptAmount: ''
      },

      paymentData: {
        bankReceiptNo: null,
        accNo: null,
        payBillNo: null,
        amount: null,
        phoneNumber: null,
        success: false,
        paymentMode: 'MPESA',
        sessionNames: null,
        sessionIdNo: null,
        sessionPhoneNumber: null
      },

      feesAndChargeItems: [],
      billItem: [],
      totalAmount: 0,
      timer: null
    };
  },

  computed: {
    progressWidth() {
      return ((this.currentStep - 1) / (this.steps.length - 1)) * 100 + '%';
    },
    isFormValid() {
      return (
          this.businessDetails.businessName &&
          this.businessDetails.buildingName &&
          this.subCounty &&
          this.ward &&
          this.businessDetails.fullNames &&
          this.businessDetails.physicalAddress &&
          this.businessDetails.ownerPhone
      );
    },
    isZeroAmount() {
      const balance = parseFloat(this.billDetails.billBalance) || 0;
      return balance === 0 || balance <= 0;
    }
  },

  watch: {
    subCounty(newVal) {
      if (newVal) {
        this.businessDetails.subCountyName = newVal.subCountyName;
        this.businessDetails.subCountyID = newVal.subCountyID;
        this.getWards();
      }
    },
    ward(newVal) {
      if (newVal) {
        this.businessDetails.wardName = newVal.wardName;
        this.businessDetails.wardID = newVal.wardID;
      }
    }
  },

  mounted() {
    this.getSubCounties();
  },

  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },

  methods: {
    formatCurrency(amount) {
      if (!amount) return '0.00';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return dateString;
      }
    },

    startNewApplication() {
      this.currentStep = 1;
      this.selectedMethod = '';
      this.searchKeyword = '';
      this.businessSearch = false;
      this.businessDetailsResult = false;
      this.noResultsFound = false;
      this.businessSearchResults = [];
      this.applicationComplete = false;
      this.billDetails = {
        id: '',
        billNo: '',
        incomeTypeID: '',
        incomeTypeDescription: '',
        costCenterNo: '',
        accountNo: '',
        feeID: '',
        detailAmount: '',
        receiptAmount: '',
        billBalance: '',
        wardID: '',
        subCountyID: '',
        status: '',
        dateCreated: '',
        description: '',
        customerPhoneNumber: ''
      };
      this.billInfo = [];
      this.businessDetails = {
        id: "",
        businessID: "",
        businessName: "",
        subCountyID: "",
        subCountyName: "",
        wardID: "",
        wardName: "",
        plotNumber: "",
        physicalAddress: "",
        buildingName: "",
        postalAddress: "",
        postalCode: "",
        fullNames: "",
        ownerPhone: "",
        ownerEmail: ""
      };
      this.subCounty = '';
      this.ward = '';
    },

    proceedToStep2() {
      this.currentStep = 2;
      this.resetSearchState();
    },

    resetSearchState() {
      this.businessSearch = false;
      this.businessDetailsResult = false;
      this.noResultsFound = false;
      this.businessSearchResults = [];
      this.searchKeyword = '';
      if (this.selectedMethod === 'register') {
        this.businessDetails = {
          id: "",
          businessID: "",
          businessName: "",
          subCountyID: "",
          subCountyName: "",
          wardID: "",
          wardName: "",
          plotNumber: "",
          physicalAddress: "",
          buildingName: "",
          postalAddress: "",
          postalCode: "",
          fullNames: "",
          ownerPhone: "",
          ownerEmail: ""
        };
      }
    },

    switchToRegister() {
      this.selectedMethod = 'register';
      this.noResultsFound = false;
    },

    searchBusinesses() {
      if (!this.searchKeyword || this.searchKeyword.trim().length < 2) {
        return;
      }

      this.businessSearch = true;
      this.businessDetailsResult = false;
      this.noResultsFound = false;

      const data = new FormData();
      data.append("function", "getBusinessRegistry");
      data.append("search", this.searchKeyword.trim());
      data.append("page", 1);
      data.append("rows_per_page", 20);

      execute(data, trade)
          .then((res) => {
            this.businessSearch = false;
            if (res.data.success && res.data.data.businesses && res.data.data.businesses.length > 0) {
              this.businessSearchResults = res.data.data.businesses;
              this.noResultsFound = false;
            } else {
              this.businessSearchResults = [];
              this.noResultsFound = true;
            }
          })
          .catch((e) => {
            this.businessSearch = false;
            this.noResultsFound = true;
            this.message = e.message;
          });
    },

    selectBusiness(business) {
      this.getBusiness(business.businessID);
    },

    backToSearchResults() {
      this.businessDetailsResult = false;
      if (this.searchKeyword && this.searchKeyword.trim().length >= 2) {
        this.searchBusinesses();
      }
    },

    getBusiness(businessID) {
      this.businessSearch = true;
      this.businessDetailsResult = false;

      const data = new FormData();
      data.append("function", "getBusiness");
      data.append("businessID", businessID);

      execute(data, trade)
          .then((res) => {
            this.businessSearch = false;
            if (res.data.success) {
              this.businessDetailsResult = true;
              this.businessSearchResults = [];
              this.businessDetails = res.data.data.business;

              this.subCounty = {
                subCountyID: this.businessDetails.subCountyID,
                subCountyName: this.businessDetails.subCountyName
              };
              this.ward = {
                wardID: this.businessDetails.wardID,
                wardName: this.businessDetails.wardName
              };
            } else {
              this.businessDetailsResult = false;
              this.message = res.data.message;
              alert(res.data.message);
            }
          })
          .catch((e) => {
            this.businessSearch = false;
            this.message = e.message;
          });
    },

    registerBusiness() {
      this.generatingInvoice = true;

      const data = new FormData();
      data.append("function", "registerBusiness");
      data.append("business", JSON.stringify(this.businessDetails));

      execute(data, liquor)
          .then((res) => {
            if (res.data.success) {
              this.businessDetails = res.data.data.business;
              const liquorBusinessID = res.data.data.business.businessID;
              this.getFeeAndChargesByTypeDescription(liquorBusinessID);
            } else {
              this.generatingInvoice = false;
              this.message = res.data.message;
              alert(res.data.message);
            }
          })
          .catch((e) => {
            this.generatingInvoice = false;
            this.message = e.message;
            alert(e.message);
          });
    },

    getFeeAndChargesByTypeDescription(liquorBusinessID) {
      const businessID = liquorBusinessID || this.businessDetails.businessID;

      if (!businessID) {
        alert("Error: Business ID is missing. Please try again.");
        this.generatingInvoice = false;
        return;
      }

      const data = new FormData();
      data.append("function", "getFeeAndChargesByTypeDescription");
      data.append("typeDescription", "LIQUOR REGISTRATION");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.feesAndChargeItems = res.data.data.feesAndCharges.map(item => {
                item.customer = "Liquor ID: " + businessID;
                return item;
              });

              this.billItem = [];
              this.addToBill(this.businessDetails.businessName);
            } else {
              this.generatingInvoice = false;
              alert(res.data.message);
            }
          })
          .catch((e) => {
            this.generatingInvoice = false;
            this.message = e.message;
            alert(e.message);
          });
    },

    addToBill(revenueStreamItem) {
      this.totalAmount = 0;

      for (const { typeDescription, feeId, feeDescription, unitFeeAmount, accountNo, costCenterNo, incomeTypeId, customer } of this.feesAndChargeItems) {
        this.totalAmount += parseFloat(unitFeeAmount);

        this.billItem.push({
          typeDescription,
          feeDescription,
          unitFeeAmount,
          accountNo,
          costCenterNo,
          incomeTypeId,
          revenueStreamItem: revenueStreamItem,
          customerName: revenueStreamItem,
          feeId,
          amount: unitFeeAmount,
          customer: customer,
          subCountyID: this.businessDetails.subCountyID || this.subCounty?.subCountyID,
          subCountyName: this.businessDetails.subCountyName || this.subCounty?.subCountyName,
          wardID: this.businessDetails.wardID || this.ward?.wardID,
          wardName: this.businessDetails.wardName || this.ward?.wardName,
          phoneNumber: this.businessDetails.ownerPhone,
          customerPhoneNumber: this.businessDetails.ownerPhone,
          description: this.businessDetails.businessName
        });
      }

      this.generateBill();
    },

    generateBill() {
      this.message = "Generating Bill...";

      const data = new FormData();
      data.append("function", "generateBill3");
      data.append("billItem", JSON.stringify(this.billItem));

      execute(data, biller)
          .then((res) => {
            this.generatingInvoice = false;

            if (res.data.success) {
              this.billDetails = res.data.data.bill.billDetails;
              this.billInfo = res.data.data.bill.billInfo;
              this.paymentData.amount = res.data.data.bill.billDetails.billBalance;
              this.paymentData.accNo = res.data.data.bill.billDetails.billNo;
              this.paymentData.payBillNo = res.data.data.billGenerated.payBillNo;
              this.phoneNumber = this.businessDetails.ownerPhone;
              this.currentStep = 3;
            } else {
              alert(res.data.message);
            }
          })
          .catch((e) => {
            this.generatingInvoice = false;
            this.message = e.message;
            alert(e.message);
          });
    },

    printBill() {
      localStorage['params'] = JSON.stringify({
        billNo: this.billDetails.billNo
      });
      const routeData = this.$router.resolve({ name: 'bill' });
      window.open(routeData.href, '_blank');
    },

    printReceipt() {
      if (this.receiptDetails.receiptNo == null) {
        alert("Receipt not available");
        return;
      }
      localStorage['params'] = JSON.stringify({
        receiptNo: this.receiptDetails.receiptNo
      });
      const routeData = this.$router.resolve({ name: 'receipt' });
      window.open(routeData.href, '_blank');
    },

    printConfirmation() {
      const establishment = this.billDetails.description || this.businessDetails.businessName;
      const phone = this.billDetails.customerPhoneNumber || this.businessDetails.ownerPhone;
      const refNo = this.billDetails.billNo;
      const date = this.formatDate(this.billDetails.dateCreated);

      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Application Confirmation - ${refNo}<` + `/title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              padding: 40px;
              background: #f8fafc;
            }
            .container {
              max-width: 600px;
              margin: 0 auto;
              background: white;
              border-radius: 16px;
              box-shadow: 0 4px 20px rgba(0,0,0,0.1);
              overflow: hidden;
            }
            .header {
              background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
              color: white;
              padding: 30px;
              text-align: center;
            }
            .header h1 {
              font-size: 24px;
              margin-bottom: 5px;
            }
            .header p {
              opacity: 0.9;
              font-size: 14px;
            }
            .success-badge {
              display: inline-flex;
              align-items: center;
              gap: 8px;
              background: #059669;
              color: white;
              padding: 8px 20px;
              border-radius: 30px;
              font-weight: 600;
              margin: 20px 0;
            }
            .success-badge svg {
              width: 20px;
              height: 20px;
            }
            .content {
              padding: 30px;
            }
            .info-section {
              background: #f8fafc;
              border-radius: 12px;
              padding: 20px;
              margin-bottom: 20px;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              padding: 12px 0;
              border-bottom: 1px solid #e2e8f0;
            }
            .info-row:last-child {
              border-bottom: none;
            }
            .info-row .label {
              color: #64748b;
              font-size: 14px;
            }
            .info-row .value {
              color: #1e293b;
              font-weight: 600;
              font-size: 14px;
              text-align: right;
            }
            .next-steps {
              background: #eff6ff;
              border-radius: 12px;
              padding: 20px;
              margin-top: 20px;
            }
            .next-steps h3 {
              color: #1e293b;
              font-size: 16px;
              margin-bottom: 12px;
            }
            .next-steps ul {
              margin: 0;
              padding-left: 20px;
            }
            .next-steps li {
              color: #475569;
              font-size: 13px;
              margin-bottom: 8px;
              line-height: 1.5;
            }
            .footer {
              text-align: center;
              padding: 20px;
              color: #64748b;
              font-size: 12px;
              border-top: 1px solid #e2e8f0;
            }
            @media print {
              body { background: white; padding: 0; }
              .container { box-shadow: none; }
            }
          <` + `/style>
        <` + `/head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Alcoholic Drinks Licence</h1>
              <p>Application Confirmation</p>
            </div>
            <div class="content">
              <div style="text-align: center;">
                <div class="success-badge">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  Application Received
                </div>
                <p style="color: #64748b; margin-bottom: 20px;">Your liquor license application has been submitted successfully.</p>
              </div>

              <div class="info-section">
                <div class="info-row">
                  <span class="label">Reference Number</span>
                  <span class="value">${refNo}</span>
                </div>
                <div class="info-row">
                  <span class="label">Establishment</span>
                  <span class="value">${establishment}</span>
                </div>
                <div class="info-row">
                  <span class="label">Phone Number</span>
                  <span class="value">${phone}</span>
                </div>
                <div class="info-row">
                  <span class="label">Date Submitted</span>
                  <span class="value">${date}</span>
                </div>
              </div>

              <div class="next-steps">
                <h3>What's Next?</h3>
                <ul>
                  <li>Your application will be reviewed by the licensing department</li>
                  <li>An inspection may be scheduled for your premises</li>
                  <li>You will be notified via SMS once your license is ready</li>
                  <li>You can track your application status using the reference number above</li>
                </ul>
              </div>
            </div>
            <div class="footer">
              <p>Keep this confirmation for your records</p>
              <p style="margin-top: 5px;">Printed on ${new Date().toLocaleString()}</p>
            </div>
          </div>
          <scr` + `ipt>window.onload = function() { window.print(); }<` + `/script>
        <` + `/body>
        <` + `/html>
      `;

      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
    },

    makePayment() {
      if (this.paymentData.paymentMode === "MPESA" && !this.phoneNumber) {
        alert("Please enter your phone number");
        return;
      }

      this.processingPayment = true;
      this.message = "Sending payment request...";
      this.paymentData.phoneNumber = this.phoneNumber;

      const data = new FormData();
      data.append("function", "makePayment");
      data.append("paymentData", JSON.stringify(this.paymentData));

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.timer = setInterval(() => {
                this.checkPayment();
              }, 3000);
            } else {
              this.processingPayment = false;
              alert(res.data.message);
            }
          })
          .catch((e) => {
            this.processingPayment = false;
            alert(e.message);
          });
    },

    checkPayment() {
      const messages = ["Waiting for payment.", "Waiting for payment..", "Waiting for payment..."];
      this.message = messages[Math.floor(Math.random() * messages.length)];

      const data = new FormData();
      data.append("function", "checkPayment");
      data.append("accountRef", this.paymentData.accNo);

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              clearInterval(this.timer);
              this.processingPayment = false;
              this.receiptDetails = res.data.data.receipt.receiptDetails;
              this.paymentStep = 'success';
            }
          })
          .catch((e) => {
            console.error(e.message);
          });
    },

    completeApplication() {
      this.showPaymentModal = false;
      this.applicationComplete = true;
    },

    getSubCounties() {
      const data = new FormData();
      data.append("function", "getSubCounty");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.subCounties = res.data.data.subCounties;
            }
          })
          .catch((e) => {
            console.error(e.message);
          });
    },

    getWards() {
      this.wards = [];
      this.ward = '';

      const data = new FormData();
      data.append("function", "getWards");
      data.append("subCountyID", this.subCounty.subCountyID);

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.wards = res.data.data.wards;
            }
          })
          .catch((e) => {
            console.error(e.message);
          });
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
}

.main-content {
  flex: 1;
  padding-top: 72px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Page Hero */
.page-hero {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  padding: 48px 0;
  color: white;
  position: relative;
  overflow: hidden;
}

.hero-background {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.hero-pattern {
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  position: relative;
  z-index: 1;
}

.breadcrumb-link {
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; }

.hero-title-section {
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
  z-index: 1;
}

.hero-icon {
  width: 64px;
  height: 64px;
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 4px;
}

.page-subtitle {
  opacity: 0.9;
  font-size: 16px;
}

/* Progress Section */
.progress-section {
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 24px 0;
  position: sticky;
  top: 72px;
  z-index: 100;
}

.progress-bar {
  position: relative;
}

.progress-track {
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  margin-bottom: 16px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #0f4c81, #059669);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-steps {
  display: flex;
  justify-content: space-between;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.step-marker {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s;
}

.progress-step.active .step-marker {
  background: #0f4c81;
  color: white;
}

.progress-step.completed .step-marker {
  background: #059669;
  color: white;
}

.step-label {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

.progress-step.active .step-label {
  color: #0f4c81;
  font-weight: 600;
}

.progress-step.completed .step-label {
  color: #059669;
}

/* Form Section */
.form-section {
  padding: 48px 0;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 32px;
  align-items: start;
}

/* Form Card */
.form-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
}

.card-header {
  padding: 24px 32px;
  border-bottom: 1px solid #e2e8f0;
}

.card-header h2 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.card-header p {
  color: #64748b;
  font-size: 14px;
}

.card-body {
  padding: 32px;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin-bottom: 16px;
  transition: color 0.2s;
}

.back-btn:hover {
  color: #0f4c81;
}

/* Method Options */
.method-options {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
}

.method-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.method-card:hover {
  border-color: #0f4c81;
  background: #f8fafc;
}

.method-card.selected {
  border-color: #0f4c81;
  background: #eff6ff;
}

.method-icon {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.method-icon.blue {
  background: #dbeafe;
  color: #2563eb;
}

.method-icon.green {
  background: #d1fae5;
  color: #059669;
}

.method-content {
  flex: 1;
}

.method-content h3 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.method-content p {
  font-size: 14px;
  color: #64748b;
}

.method-check {
  width: 32px;
  height: 32px;
  background: #059669;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

/* Search Box */
.search-box {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.search-input-wrapper {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.search-input-wrapper > svg {
  position: absolute;
  left: 16px;
  color: #94a3b8;
}

.search-input {
  width: 100%;
  padding: 14px 48px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #0f4c81;
  box-shadow: 0 0 0 3px rgba(15, 76, 129, 0.1);
}

.clear-btn {
  position: absolute;
  right: 12px;
  padding: 8px;
  background: #f1f5f9;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #64748b;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 48px 24px;
}

.loader-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #0f4c81;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Search Results */
.search-results {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.results-count {
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
}

.results-hint {
  color: #64748b;
  font-size: 13px;
}

.results-list {
  max-height: 400px;
  overflow-y: auto;
}

.result-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background 0.2s;
}

.result-item:last-child {
  border-bottom: none;
}

.result-item:hover {
  background: #f8fafc;
}

.result-icon {
  width: 48px;
  height: 48px;
  background: #dbeafe;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
}

.result-content {
  flex: 1;
}

.result-content h4 {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.result-content p {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
}

.result-tag {
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 4px;
  color: #64748b;
}

.result-location {
  color: #64748b;
}

.result-action {
  color: #94a3b8;
}

/* No Results */
.no-results {
  text-align: center;
  padding: 48px 24px;
}

.no-results-icon {
  width: 80px;
  height: 80px;
  background: #fee2e2;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: #dc2626;
}

.no-results h3 {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.no-results p {
  color: #64748b;
  margin-bottom: 24px;
}

/* Selected Business */
.selected-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.back-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #64748b;
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;
  background: none;
  border: none;
}

.back-link:hover {
  color: #0f4c81;
}

.selected-badge {
  background: #d1fae5;
  color: #059669;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.business-details-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.business-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.business-avatar {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #0f4c81, #0a3459);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  font-weight: 700;
}

.business-title h3 {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.business-id {
  color: #64748b;
  font-size: 14px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1px;
  background: #e2e8f0;
}

.detail-item {
  padding: 16px 20px;
  background: white;
}

.detail-item.full-width {
  grid-column: span 2;
}

.detail-item label {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-item span {
  font-size: 14px;
  color: #1e293b;
  font-weight: 500;
}

/* Form Grid */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: span 2;
}

.form-label {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 8px;
}

.form-label.required::after {
  content: ' *';
  color: #dc2626;
}

.form-input,
.form-select,
.form-textarea {
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  font-family: 'DM Sans', sans-serif;
  transition: all 0.2s;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #0f4c81;
  box-shadow: 0 0 0 3px rgba(15, 76, 129, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  text-decoration: none;
}

.btn-primary {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(15, 76, 129, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-outline {
  background: white;
  border: 2px solid #e2e8f0;
  color: #1e293b;
}

.btn-outline:hover {
  border-color: #0f4c81;
  color: #0f4c81;
}

.btn-lg {
  padding: 16px 32px;
  font-size: 16px;
}

.btn-block {
  width: 100%;
}

.form-actions {
  margin-top: 32px;
  display: flex;
  justify-content: flex-end;
}

.form-actions.split {
  justify-content: space-between;
}

.loading-text {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner-sm {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Invoice Card */
.invoice-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.invoice-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.invoice-title {
  display: flex;
  flex-direction: column;
}

.invoice-label {
  font-size: 12px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.invoice-number {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #0f4c81;
}

.invoice-status {
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
}

.invoice-status.pending {
  background: #fef3c7;
  color: #d97706;
}

.invoice-details {
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.invoice-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
}

.invoice-row .label {
  color: #64748b;
  font-size: 14px;
}

.invoice-row .value {
  color: #1e293b;
  font-weight: 500;
  font-size: 14px;
}

.invoice-items {
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.items-header {
  display: flex;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.item-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
}

.item-row:last-child {
  border-bottom: none;
}

.item-desc {
  color: #1e293b;
  font-size: 14px;
}

.item-amount {
  font-weight: 600;
  color: #1e293b;
}

.invoice-total {
  padding: 20px;
  background: #f8fafc;
}

.total-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 14px;
}

.total-row span:first-child {
  color: #64748b;
}

.total-row.final {
  padding-top: 16px;
  margin-top: 8px;
  border-top: 2px solid #e2e8f0;
}

.total-amount {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 24px;
  font-weight: 800;
  color: #059669;
}

/* Success Card */
.success-card {
  text-align: center;
}

.success-content {
  padding: 48px 32px;
}

.success-icon {
  width: 80px;
  height: 80px;
  background: #d1fae5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: #059669;
}

.success-content h2 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 24px;
  font-weight: 800;
  color: #059669;
  margin-bottom: 8px;
}

.success-content > p {
  color: #64748b;
  margin-bottom: 32px;
}

.receipt-info {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 32px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #e2e8f0;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row span {
  color: #64748b;
  font-size: 14px;
}

.info-row strong {
  color: #1e293b;
}

.next-steps {
  text-align: left;
  background: #eff6ff;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 32px;
}

.next-steps h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
}

.next-steps ul {
  margin: 0;
  padding-left: 20px;
}

.next-steps li {
  color: #64748b;
  font-size: 14px;
  margin-bottom: 8px;
}

.success-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

/* Info Column */
.info-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Quick Actions Card */
.quick-actions-card {
  border: 2px solid #fef3c7;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
}

.quick-actions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.quick-action-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.2s;
}

.quick-action-item:hover {
  border-color: #0f4c81;
  box-shadow: 0 4px 12px rgba(15, 76, 129, 0.1);
  transform: translateX(4px);
}

.quick-action-icon {
  width: 48px;
  height: 48px;
  background: #dbeafe;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  flex-shrink: 0;
}

.quick-action-icon.orange {
  background: #ffedd5;
  color: #ea580c;
}

.quick-action-icon.green {
  background: #d1fae5;
  color: #059669;
}

.quick-action-content {
  flex: 1;
  min-width: 0;
}

.quick-action-title {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
}

.quick-action-desc {
  display: block;
  font-size: 12px;
  color: #64748b;
}

.quick-action-arrow {
  color: #94a3b8;
  flex-shrink: 0;
  transition: transform 0.2s, color 0.2s;
}

.quick-action-item:hover .quick-action-arrow {
  color: #0f4c81;
  transform: translateX(4px);
}

.info-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  color: #0f4c81;
}

.info-card-header h3 {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.requirements-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.requirements-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  font-size: 14px;
  color: #475569;
  border-bottom: 1px solid #f1f5f9;
}

.requirements-list li:last-child {
  border-bottom: none;
}

.requirements-list svg {
  color: #059669;
  flex-shrink: 0;
}

.process-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.process-item {
  display: flex;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 8px;
}

.process-label {
  color: #64748b;
  font-size: 14px;
}

.process-time {
  color: #1e293b;
  font-weight: 600;
  font-size: 14px;
}

.help-card {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #bfdbfe;
}

.help-card p {
  color: #64748b;
  font-size: 14px;
  margin-bottom: 16px;
}

.help-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0f4c81;
  font-weight: 600;
  text-decoration: none;
  font-size: 14px;
}

.help-link:hover {
  gap: 12px;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
}

.modal-container {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
}

.modal-close {
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
}

.modal-body {
  padding: 24px;
}

/* Payment Methods */
.payment-methods {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.payment-method {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.payment-method:hover {
  border-color: #0f4c81;
}

.payment-method.selected {
  border-color: #0f4c81;
  background: #eff6ff;
}

.method-logo {
  height: 32px;
  width: auto;
}

.method-info h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
}

.method-info p {
  font-size: 13px;
  color: #64748b;
}

.phone-input-section {
  margin-top: 16px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
}

.phone-input-section label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 8px;
}

.phone-input-wrapper {
  display: flex;
  align-items: center;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
}

.phone-prefix {
  padding: 12px 16px;
  background: #f1f5f9;
  color: #64748b;
  font-weight: 500;
  border-right: 2px solid #e2e8f0;
}

.phone-input {
  flex: 1;
  padding: 12px 16px;
  border: none;
  font-size: 16px;
  font-family: 'DM Sans', sans-serif;
}

.phone-input:focus {
  outline: none;
}

/* Payment Confirm */
.confirm-summary {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-row span:first-child {
  color: #64748b;
}

.summary-row span:last-child {
  color: #1e293b;
  font-weight: 500;
}

.summary-row.total {
  margin-top: 8px;
  padding-top: 16px;
  border-top: 2px solid #e2e8f0;
  border-bottom: none;
}

.summary-row.total span:last-child {
  font-size: 18px;
  font-weight: 700;
  color: #059669;
}

.confirm-note {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #fef3c7;
  border-radius: 10px;
  margin-bottom: 20px;
}

.confirm-note svg {
  color: #d97706;
  flex-shrink: 0;
}

.confirm-note p {
  font-size: 13px;
  color: #92400e;
  line-height: 1.5;
}

.payment-message {
  text-align: center;
  color: #0f4c81;
  font-weight: 500;
  margin-bottom: 16px;
}

.confirm-actions {
  display: flex;
  gap: 12px;
}

.confirm-actions .btn {
  flex: 1;
}

/* Payment Success */
.payment-success {
  text-align: center;
  padding: 24px 0;
}

.success-icon-lg {
  width: 100px;
  height: 100px;
  background: #d1fae5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: #059669;
}

.payment-success h3 {
  font-size: 24px;
  font-weight: 700;
  color: #059669;
  margin-bottom: 8px;
}

.payment-success p {
  color: #64748b;
  margin-bottom: 8px;
}

.receipt-number {
  margin-bottom: 24px;
}

/* Responsive */
@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .info-column {
    order: -1;
    flex-direction: row;
    flex-wrap: wrap;
  }

  .info-card {
    flex: 1;
    min-width: 280px;
  }
}

@media (max-width: 768px) {
  .container { padding: 0 12px; }

  /* Hero shrunk. */
  .page-hero { padding: 20px 0 40px !important; }
  .page-title { font-size: 20px !important; margin-bottom: 4px; }
  .hero-subtitle,
  .breadcrumb,
  .breadcrumb-current { font-size: 12px; }
  .hero-title-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  .progress-section { padding: 12px 0 !important; }
  .form-section { padding: 24px 0 32px !important; }

  /* Progress stepper: keep one row, shrink circles, hide labels. */
  .progress-steps {
    flex-wrap: nowrap;
    gap: 4px;
  }
  .progress-steps .step,
  .progress-steps .progress-step { flex: 1; min-width: 0; }
  .progress-steps .step-number,
  .progress-steps .step-circle {
    width: 26px !important;
    height: 26px !important;
    font-size: 12px !important;
  }
  .step-label { display: none; }
  .step-connector { display: none; }

  /* Card chrome (multi-step wizard cards). */
  .card,
  .form-card,
  .step-card {
    border-radius: 12px;
  }
  .card-header,
  .form-card-header { padding: 12px 14px; }
  .card-body,
  .form-card-body { padding: 16px 14px !important; }
  .step-title,
  .card-title { font-size: 16px; }
  .step-description,
  .card-subtitle { font-size: 12.5px; line-height: 1.4; }

  /* Form fields. */
  .form-grid { grid-template-columns: 1fr; gap: 10px; }
  .form-group { margin-bottom: 12px; }
  .form-group.full-width { grid-column: span 1; }
  .form-label { font-size: 12.5px; margin-bottom: 4px; }
  .form-input,
  .form-select,
  .form-textarea { font-size: 14px; padding: 10px 12px; min-height: 42px; }
  .form-textarea { min-height: 90px; }

  /* Details / review grids. */
  .details-grid { grid-template-columns: 1fr; gap: 10px; }
  .detail-item.full-width { grid-column: span 1; }
  .detail-item { padding: 10px 12px; }
  .detail-item .detail-label { font-size: 11.5px; }
  .detail-item .detail-value { font-size: 13.5px; }

  /* Method-selection tiles (renewal vs new etc). */
  .method-card {
    flex-direction: column;
    text-align: center;
    padding: 12px 10px;
    position: relative;
  }
  .method-check { position: absolute; top: 8px; right: 8px; }
  .method-card h4,
  .method-card .method-title { font-size: 13px; line-height: 1.2; margin-top: 6px; }
  .method-card p,
  .method-card .method-desc { font-size: 11.5px; line-height: 1.35; }

  /* Action rows. */
  .form-actions,
  .form-actions.split,
  .success-actions {
    flex-direction: column;
    gap: 8px;
  }
  .form-actions .btn,
  .success-actions .btn { width: 100%; padding: 12px 16px; font-size: 13.5px; }

  /* Info side-column (secondary sidebar in some steps). */
  .info-column { flex-direction: column; gap: 10px; }
  .info-card { min-width: 100%; padding: 14px; }

  /* Tables inside the wizard (e.g. summary of selected licences) — scroll if wide. */
  .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  table { font-size: 12px; }
  table th, table td { padding: 8px 10px; white-space: nowrap; }
}

@media (max-width: 480px) {
  .container { padding: 0 10px; }
  .page-title { font-size: 18px !important; }
  .card-body,
  .form-card-body { padding: 14px 12px !important; }
  .form-input, .form-select, .form-textarea { font-size: 13.5px; min-height: 40px; }
  .step-title, .card-title { font-size: 15px; }
  .progress-steps .step-number,
  .progress-steps .step-circle { width: 22px !important; height: 22px !important; font-size: 11px !important; }
}
</style>